import React from 'react';
import { Box, Typography, useTheme, useMediaQuery, Modal, CircularProgress } from '@mui/material';
import { Assistant } from '@mui/icons-material';
import Button from './Button';
import IconButton from './IconButton';
import { ICON_BUTTON_TYPE } from '../constants';
import { useTranslation } from 'react-i18next';

const SmartMedCheckModal = ({ open, onClose, analysisData, isLoading }) => {
  const theme = useTheme();
  const { t, i18n } = useTranslation();
  function LastUpdated(dateStr) {
    // Convert date string to Date object
    const dateObj = new Date(dateStr);

    // Format date based on active language
    const formattedDate = new Intl.DateTimeFormat(i18n.language, {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    }).format(dateObj);
    return formattedDate;
  }
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  return (
    <Modal
      open={open}
      onClose={onClose}
      aria-labelledby="smart-med-check-modal"
      aria-describedby="smart-med-check-analysis"
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          width: isMobile ? '90%' : '600px',
          maxHeight: '80vh',
          p: 4,
          bgcolor: 'background.paper',
          borderRadius: 2,
          // position: 'relative',
          // top: '50%',
          // left: '50%',
          // transform: 'translate(-50%, -50%)',
          overflow: 'auto',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 3,
          }}
        >
          <Typography variant="h5" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Assistant sx={{ color: '#26786a' }} />
            {t('smartMedication')}
          </Typography>
          <IconButton type={ICON_BUTTON_TYPE.close} onClick={onClose} />
        </Box>

        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4 }}>
            <CircularProgress size={40} style={{ color: '#26786a' }} />
            <Typography variant="body1" sx={{ ml: 2 }}>
              {t('analyzingYourMedications')}
            </Typography>
          </Box>
        ) : analysisData ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {analysisData.summary && (
              <Box>
                <Typography variant="h6" sx={{ mb: 1, color: '#26786a' }}>
                  {t('analysisSummary')}
                </Typography>
                <Box sx={{ p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    <strong>{t('interactionsFound')}:</strong> {analysisData.summary.interaction_count || 0}
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    <strong>{t('allergyConflicts')}:</strong> {analysisData.summary.allergy_conflicts || 0}
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    <strong>{t('duplicateMedications')}:</strong> {analysisData.summary.duplicate_medications || 0}
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#666', fontSize: '0.875rem' }}>
                    <strong>{t('lastUpdated')}:</strong>{' '}
                    {LastUpdated(analysisData.summary.last_updated || analysisData.last_updated)}
                  </Typography>
                </Box>
              </Box>
            )}

            {analysisData.issues && analysisData.issues.length > 0 && (
              <Box>
                <Typography variant="h6" sx={{ mb: 1, color: '#d32f2f' }}>
                  {t('issuesFound')}
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {analysisData.issues.map((issue, index) => (
                    <Box key={index} sx={{ p: 2, bgcolor: '#ffebee', borderRadius: 1, border: '1px solid #ffcdd2' }}>
                      <Typography variant="body2" sx={{ fontWeight: 500, color: '#d32f2f' }}>
                        {issue.title || `Issue ${index + 1}`}
                      </Typography>
                      <Typography variant="body2" sx={{ mt: 0.5, color: '#666' }}>
                        {issue.description || issue}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Box>
            )}

            {analysisData.recommendations && analysisData.recommendations.length > 0 && (
              <Box>
                <Typography variant="h6" sx={{ mb: 1, color: '#26786a' }}>
                  {t('foodAnalysisObj.recommendations')}
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {analysisData.recommendations.map((rec, index) => (
                    <Box key={index} sx={{ p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {rec.title ||
                          `
                        ${t('foodAnalysisObj.recommendations')} ${index + 1}`}
                      </Typography>
                      <Typography variant="body2" sx={{ mt: 0.5, color: '#666' }}>
                        {rec.description || rec}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Box>
            )}

            {analysisData.warnings && analysisData.warnings.length > 0 && (
              <Box>
                <Typography variant="h6" sx={{ mb: 1, color: '#d32f2f' }}>
                  {t('warnings')}
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {analysisData.warnings.map((warning, index) => (
                    <Box key={index} sx={{ p: 2, bgcolor: '#ffebee', borderRadius: 1, border: '1px solid #ffcdd2' }}>
                      <Typography variant="body2" sx={{ fontWeight: 500, color: '#d32f2f' }}>
                        {warning.title}
                      </Typography>
                      <Typography variant="body2" sx={{ mt: 0.5, color: '#666' }}>
                        {warning.description}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Box>
            )}

            {analysisData.interactions && analysisData.interactions.length > 0 && (
              <Box>
                <Typography variant="h6" sx={{ mb: 1, color: '#ed6c02' }}>
                  {t('drugInteractions')}
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {analysisData.interactions.map((interaction, index) => (
                    <Box key={index} sx={{ p: 2, bgcolor: '#fff3e0', borderRadius: 1, border: '1px solid #ffcc02' }}>
                      <Typography variant="body2" sx={{ fontWeight: 500, color: '#ed6c02' }}>
                        {interaction.medication1} + {interaction.medication2}
                      </Typography>
                      <Typography variant="body2" sx={{ mt: 0.5, color: '#666' }}>
                        {interaction.description}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Box>
            )}

            {analysisData.error && (
              <Box sx={{ p: 2, bgcolor: '#ffebee', borderRadius: 1, border: '1px solid #ffcdd2' }}>
                <Typography variant="body2" sx={{ color: '#d32f2f' }}>
                  {analysisData.error}
                </Typography>
              </Box>
            )}

            {/* Additional metadata */}
            {analysisData.medication_count !== undefined && (
              <Box sx={{ p: 2, bgcolor: '#e8f5e8', borderRadius: 1 }}>
                <Typography variant="body2" sx={{ color: '#2e7d32' }}>
                  <strong>{t('analysisDetails')}:</strong> {analysisData.medication_count} {t('medications')},{' '}
                  {analysisData.condition_count || 0} {t('conditions')}, {analysisData.allergy_count || 0}{' '}
                  {t('allergiesAnalyzed')}
                </Typography>
              </Box>
            )}
          </Box>
        ) : (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="body1" color="text.secondary">
              {'noAnalysisDataAvailable'}
            </Typography>
          </Box>
        )}

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
          <Button text={t('close')} bg="light" onClick={onClose} />
        </Box>
      </Box>
    </Modal>
  );
};

export default SmartMedCheckModal;
